/**
 * Shared utilities + tiny presentational primitives for the Content section.
 * Mirrors users-and-agents/shared.tsx so each tab focuses on filters + table
 * markup. Kept local to this section.
 */

import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export const DASH = '—';

/** Browser-local timestamp formatter. Returns DASH for falsy input. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface DataTableShellProps {
  loading?: boolean;
  error?: unknown;
  empty?: boolean;
  emptyLabel?: string;
  children: ReactNode;
}

export function DataTableShell({
  loading,
  error,
  empty,
  emptyLabel = 'No results',
  children,
}: DataTableShellProps) {
  if (error) {
    const message =
      error instanceof Error ? error.message : typeof error === 'string' ? error : 'Request failed';
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
        {message}
      </div>
    );
  }
  if (loading) {
    return (
      <div className="rounded-md border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (empty) {
    return (
      <div className="rounded-md border border-dashed bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
        {emptyLabel}
      </div>
    );
  }
  return <div className="overflow-hidden rounded-md border bg-card">{children}</div>;
}

interface DataTableProps<T> {
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  columns: Array<{
    key: string;
    label: string;
    render: (row: T) => ReactNode;
    className?: string;
    align?: 'left' | 'right' | 'center';
  }>;
}

export function DataTable<T>({ rows, rowKey, onRowClick, columns }: DataTableProps<T>) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  'px-4 py-2 text-left font-medium text-muted-foreground',
                  c.align === 'right' && 'text-right',
                  c.align === 'center' && 'text-center',
                  c.className,
                )}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={cn(
                'border-t transition-colors hover:bg-muted/30',
                onRowClick && 'cursor-pointer',
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'px-4 py-2 align-top',
                    c.align === 'right' && 'text-right',
                    c.align === 'center' && 'text-center',
                    c.className,
                  )}
                >
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
}

export function Pagination({ total, limit, offset, onChange }: PaginationProps) {
  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <div className="flex items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground">
      <span>
        {total === 0 ? '0 of 0' : `${offset + 1}–${Math.min(offset + limit, total)} of ${total}`}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="rounded-md border px-2 py-1 disabled:opacity-50"
          disabled={!canPrev}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Prev
        </button>
        <span className="px-2">
          Page {page} / {totalPages}
        </span>
        <button
          type="button"
          className="rounded-md border px-2 py-1 disabled:opacity-50"
          disabled={!canNext}
          onClick={() => onChange(offset + limit)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}

export function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
  return (
    <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
      <span>{label}</span>
      <select
        className="h-9 rounded-md border bg-background px-2 text-sm text-foreground"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

interface DateRangeProps {
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
}

export function DateRange({ from, to, onChange }: DateRangeProps) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <label className="flex items-center gap-1">
        <span>From</span>
        <input
          type="date"
          className="h-9 rounded-md border bg-background px-2 text-sm text-foreground"
          value={from}
          onChange={(e) => onChange({ from: e.target.value, to })}
        />
      </label>
      <label className="flex items-center gap-1">
        <span>To</span>
        <input
          type="date"
          className="h-9 rounded-md border bg-background px-2 text-sm text-foreground"
          value={to}
          onChange={(e) => onChange({ from, to: e.target.value })}
        />
      </label>
    </div>
  );
}

export function toIsoStart(d: string): string | undefined {
  if (!d) return undefined;
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
}

export function toIsoEnd(d: string): string | undefined {
  if (!d) return undefined;
  const dt = new Date(`${d}T23:59:59`);
  return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
}
